import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { X, Flame, Leaf, Star, AlertCircle } from 'lucide-react';
import { useKiosk } from '../../context/KioskContext';
import { formatCurrency, formatPriceModifier } from '../../utils/formatCurrency';
import { calculateItemTotal } from '../../utils/calculateTotals';
import ModifierGroup from './ModifierGroup';
import QuantitySelector from './QuantitySelector';
import { toast } from 'sonner';
import NoImageTile from '@/components/NoImageTile';

/**
 * ItemDetailModal - Full-screen item customization modal
 * 
 * Features:
 * - Large food image
 * - Item name, description, base price
 * - Modifier groups (required/optional)
 * - Special instructions
 * - Quantity selector
 * - Live price calculation
 * - Validation for required modifiers
 * - Support for editing existing cart items
 */
export default function ItemDetailModal({
  item,
  editingCartItem = null, // For editing existing cart items
  onClose,
  onAddToCart,
}) {
  const { addToCart, updateCartItem } = useKiosk();

  // Initialize state from editingCartItem or defaults
  const [selectedModifiers, setSelectedModifiers] = useState(() => {
    if (editingCartItem?.modifiers) {
      // Group existing modifiers by group ID
      const grouped = {};
      editingCartItem.modifiers.forEach(mod => {
        const groupId = mod.groupId;
        if (!grouped[groupId]) grouped[groupId] = [];
        grouped[groupId].push(mod);
      });
      return grouped;
    }
    return {};
  });

  const [quantity, setQuantity] = useState(editingCartItem?.quantity || 1);
  const [instructions, setInstructions] = useState(editingCartItem?.instructions || '');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Character limit for instructions
  const INSTRUCTIONS_LIMIT = 200;

  // Destructure item data
  const {
    id,
    name,
    description,
    price,
    image_url,
    is_available = true,
    is_popular,
    is_spicy,
    is_vegetarian,
    calories,
    allergens,
    modifier_groups = [],
  } = item || {};

  // Check if this is an edit
  const isEditing = !!editingCartItem;

  // The item's own photo, or a neutral tile (never a stock photo)
  const [imageFailed, setImageFailed] = useState(false);
  const hasPhoto = !!image_url && !imageFailed;

  // Get all selected modifiers as flat array
  const allSelectedModifiers = useMemo(() => {
    return Object.values(selectedModifiers).flat();
  }, [selectedModifiers]);

  // Calculate price
  const priceBreakdown = useMemo(() => {
    return calculateItemTotal(price, allSelectedModifiers, quantity);
  }, [price, allSelectedModifiers, quantity]);

  // Validation: check all required groups are satisfied
  const validation = useMemo(() => {
    const errors = [];
    const requiredGroups = modifier_groups.filter(g => g.required);
    
    for (const group of requiredGroups) {
      const groupSelections = selectedModifiers[group.id] || [];
      const minRequired = group.min_selections || 1;
      
      // Check if enough available options exist
      const availableCount = group.options?.filter(o => o.is_available !== false).length || 0;
      
      if (availableCount < minRequired) {
        errors.push({
          groupId: group.id,
          groupName: group.name,
          type: 'unavailable',
          message: `${group.name}: Not enough options available`,
        });
      } else if (groupSelections.length < minRequired) {
        errors.push({
          groupId: group.id,
          groupName: group.name,
          type: 'incomplete',
          message: `${group.name}: Please make a selection`,
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      hasUnavailableRequired: errors.some(e => e.type === 'unavailable'),
    };
  }, [modifier_groups, selectedModifiers]);

  // Handle modifier selection change
  const handleModifierChange = useCallback((groupId, selections) => {
    setSelectedModifiers(prev => ({
      ...prev,
      [groupId]: selections.map(opt => ({
        id: opt.id,
        groupId,
        name: opt.name,
        price: opt.price || 0,
      })),
    }));
  }, []);

  // Handle add to cart
  const handleSubmit = useCallback(() => {
    if (!validation.isValid || isSubmitting) return;

    setIsSubmitting(true);

    // Small delay for animation
    setTimeout(() => {
      if (isEditing && editingCartItem) {
        // Update existing cart item
        updateCartItem(editingCartItem.id, {
          modifiers: allSelectedModifiers,
          quantity,
          instructions: instructions.trim(),
          totalPrice: priceBreakdown.unitPrice,
          price: priceBreakdown.unitPrice, // backward compat
        });
        // Note: Toast shown by parent CartPage
      } else {
        // Add new item to cart
        addToCart(item, allSelectedModifiers, quantity, instructions.trim());
        toast.success(`Added ${name}`);
      }

      onAddToCart?.({
        item,
        modifiers: allSelectedModifiers,
        quantity,
        instructions: instructions.trim(),
        totalPrice: priceBreakdown.lineTotal,
      });

      onClose();
    }, 200);
  }, [
    validation.isValid,
    isSubmitting,
    isEditing,
    editingCartItem,
    item,
    name,
    allSelectedModifiers,
    quantity,
    instructions,
    priceBreakdown,
    addToCart,
    updateCartItem,
    onAddToCart,
    onClose,
  ]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!item) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col bg-[#0f1419]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Image Section */}
        <div className="relative aspect-[16/9] md:aspect-[21/9] bg-[#1a1f26]">
          {/* Skeleton */}
          {hasPhoto && !imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-[#1a1f26] via-[#252b33] to-[#1a1f26]" />
          )}

          {/* Image */}
          {hasPhoto ? (
          <img
            src={image_url}
            alt={name}
            onLoad={() => setImageLoaded(true)}
            onError={() => { setImageFailed(true); setImageLoaded(true); }}
            className={`
              w-full h-full object-cover
              transition-opacity duration-300
              ${imageLoaded ? 'opacity-100' : 'opacity-0'}
            `}
          />
          ) : (
            <NoImageTile name={name} dark className="absolute inset-0 w-full h-full" />
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-transparent to-transparent" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="
              absolute top-4 right-4
              w-12 h-12 rounded-full
              bg-black/50 backdrop-blur-sm
              flex items-center justify-center
              text-white hover:bg-black/70
              transition-colors
            "
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Tags */}
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
            {is_popular && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-[#f5a623] text-[#0f1419] text-sm font-semibold rounded-full">
                <Star size={14} fill="currentColor" />
                Popular
              </span>
            )}
            {is_spicy && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-[#ef4444] text-white text-sm font-semibold rounded-full">
                <Flame size={14} />
                Spicy
              </span>
            )}
            {is_vegetarian && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-[#10b981] text-white text-sm font-semibold rounded-full">
                <Leaf size={14} />
                Vegetarian
              </span>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-6 max-w-3xl mx-auto">
          {/* Item Info */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4">
              <h1 
                id="modal-title"
                className="text-3xl font-bold text-white font-['Outfit']"
              >
                {name}
              </h1>
              <span className="text-2xl font-bold text-[#f5a623] whitespace-nowrap">
                {formatCurrency(price)}
              </span>
            </div>

            {description && (
              <p className="mt-3 text-lg text-gray-400 leading-relaxed">
                {description}
              </p>
            )}

            {/* Additional info */}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
              {calories && (
                <span>{calories} cal</span>
              )}
              {allergens && (
                <span>Contains: {allergens}</span>
              )}
            </div>
          </div>

          {/* Unavailable Required Warning */}
          {validation.hasUnavailableRequired && (
            <div className="mb-6 p-4 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/20">
              <div className="flex items-start gap-3">
                <AlertCircle size={24} className="text-[#ef4444] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#ef4444]">Unable to add this item</h3>
                  <p className="text-sm text-[#ef4444]/80 mt-1">
                    Some required options are currently unavailable. Please choose a different item.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Modifier Groups */}
          {modifier_groups.length > 0 && (
            <div className="space-y-8 mb-8">
              {modifier_groups.map((group) => (
                <div key={group.id} className="border-t border-white/10 pt-6">
                  <ModifierGroup
                    group={group}
                    selectedOptions={selectedModifiers[group.id] || []}
                    onSelectionChange={handleModifierChange}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Special Instructions */}
          <div className="border-t border-white/10 pt-6 mb-8">
            <label 
              htmlFor="instructions"
              className="block text-lg font-semibold text-white mb-3"
            >
              Special Instructions
              <span className="text-sm font-normal text-gray-400 ml-2">Optional</span>
            </label>
            <textarea
              id="instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value.slice(0, INSTRUCTIONS_LIMIT))}
              placeholder="Allergies, preferences, or special requests..."
              rows={3}
              className="
                w-full px-4 py-3
                bg-[#1a1f26] border-2 border-white/10
                rounded-xl
                text-white text-base
                placeholder-gray-500
                resize-none
                focus:border-[#f5a623] focus:outline-none
                transition-colors
              "
            />
            <div className="flex justify-end mt-2">
              <span className={`text-sm ${instructions.length >= INSTRUCTIONS_LIMIT ? 'text-[#ef4444]' : 'text-gray-500'}`}>
                {instructions.length}/{INSTRUCTIONS_LIMIT}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="
        sticky bottom-0
        bg-[#1a1f26]/98 backdrop-blur-md
        border-t border-white/10
        shadow-[0_-4px_20px_rgba(0,0,0,0.3)]
        px-6 py-4
      ">
        <div className="max-w-3xl mx-auto">
          {/* Price Breakdown (when modifiers selected) */}
          {allSelectedModifiers.length > 0 && (
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-gray-400">
                Base: {formatCurrency(price)}
                {priceBreakdown.modifierTotal > 0 && (
                  <span className="ml-2">
                    + Extras: {formatPriceModifier(priceBreakdown.modifierTotal)}
                  </span>
                )}
              </span>
              <span className="text-gray-400">
                {formatCurrency(priceBreakdown.unitPrice)} × {quantity}
              </span>
            </div>
          )}

          {/* Main Controls */}
          <div className="flex items-center gap-4">
            {/* Quantity Selector */}
            <QuantitySelector
              value={quantity}
              onChange={setQuantity}
              min={1}
              max={99}
              size="large"
            />

            {/* Add to Cart Button */}
            <button
              onClick={handleSubmit}
              disabled={!validation.isValid || isSubmitting || validation.hasUnavailableRequired}
              className={`
                flex-1 h-16
                flex items-center justify-center gap-3
                rounded-2xl
                font-semibold text-xl
                transition-all duration-200
                ${validation.isValid && !validation.hasUnavailableRequired
                  ? 'bg-gradient-to-r from-[#f5a623] to-[#ff6b35] text-[#0f1419] shadow-lg shadow-[#f5a623]/30 hover:brightness-110 active:scale-[0.99]'
                  : 'bg-[#252b33] text-gray-500 cursor-not-allowed'
                }
              `}
              aria-disabled={!validation.isValid}
            >
              {isSubmitting ? (
                <span>Adding...</span>
              ) : (
                <>
                  <span>{isEditing ? 'Update Item' : 'Add to Order'}</span>
                  <span className="font-bold">
                    {formatCurrency(priceBreakdown.lineTotal)}
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Validation Hint */}
          {!validation.isValid && !validation.hasUnavailableRequired && (
            <p className="text-center text-sm text-[#f5a623] mt-3">
              Please complete all required selections above
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
